import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function FontLoader() {
  useEffect(() => {
    if (document.getElementById('verdict-fonts')) return;
    const link = document.createElement('link');
    link.id = 'verdict-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,300;8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

const V = {
  ink:'#0c1830',ink2:'#2a3956',ink3:'#5a6680',ink4:'#8a93a6',
  paper:'#fbfaf6',paper2:'#f3f1ea',paper3:'#e6e2d5',line:'#d8d3c2',
  gold:'#c8a64b',good:'#2a6f4f',goodSoft:'#e8f0eb',
  bad:'#9c2b1a',badSoft:'#f4e8e5',warn:'#a8731a',warnSoft:'#f5ecd9',
  serif:"'Source Serif 4','Times New Roman',Georgia,serif",
  sans:"'Inter',-apple-system,system-ui,sans-serif",
  mono:"'JetBrains Mono',ui-monospace,monospace",
};

function getStatusCfg(status) {
  return {
    active:  { label:'Active',  color:V.good, bg:V.goodSoft },
    on_hold: { label:'On Hold', color:V.warn, bg:V.warnSoft },
    closed:  { label:'Closed',  color:V.ink4, bg:V.paper3   },
  }[status] || { label:'Active', color:V.good, bg:V.goodSoft };
}

// ─── TICKER ──────────────────────────────────────────────────────────────────

function LiveTicker({ projects }) {
  const tickerRef = useRef(null);
  const insights  = projects.filter(p => p.ai_insight && p.document_count > 0);
  const expiring  = projects.filter(p => p.has_expiring);
  const items = [];
  insights.forEach(p => items.push({ text: p.ai_insight, type: 'info' }));
  if (expiring.length > 0) items.push({ text: `${expiring.length} quotation${expiring.length > 1 ? 's' : ''} expiring soon — review required`, type: 'warn' });
  if (items.length === 0) items.push({ text: 'Upload documents to activate live intelligence across your negotiations', type: 'info' });
  const doubled = [...items, ...items];

  useEffect(() => {
    const el = tickerRef.current;
    if (!el) return;
    let pos = 0;
    const halfW = el.scrollWidth / 2;
    const tick = () => { pos += 0.4; if (pos >= halfW) pos = 0; el.style.transform = `translateX(-${pos}px)`; requestAnimationFrame(tick); };
    const id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [items.length]);

  return (
    <div style={s.utilBar}>
      <div style={s.utilLeft}><div style={s.utilDot}></div></div>
      <div style={s.tickerLabel}>LIVE INTEL</div>
      <div style={s.tickerWrap}>
        <div ref={tickerRef} style={s.tickerInner}>
          {doubled.map((item, i) => (
            <span key={i} style={s.tickerItem}>
              <span style={{ ...s.tickerText, color: item.type === 'warn' ? '#e8834a' : 'rgba(251,250,246,0.7)' }} dangerouslySetInnerHTML={{ __html: item.text }} />
              <span style={s.tickerSep}>·</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── TOP NAV ─────────────────────────────────────────────────────────────────

function TopNav({ user, logout, onNewProject }) {
  const navigate = useNavigate();
  const navItems = [
    { label: 'Negotiations', path: '/' },
    { label: 'The Room',     path: null },
    { label: 'Suppliers',    path: '/suppliers' },
    { label: 'Documents',    path: null },
    { label: 'Reports',      path: null },
    { label: 'Intelligence', path: '/intelligence' },
  ];
  return (
    <div style={s.nav}>
      <div style={s.wordmark}><span style={s.wordmarkV}>V</span><span style={s.wordmarkRest}>erdict</span></div>
      <div style={s.wordmarkDivider}></div>
      <div style={s.wordmarkTag}>Negotiation Intelligence</div>
      <div style={s.navItems}>
        {navItems.map(item => (
          <div key={item.label}
            style={{ ...s.navItem, color: item.label === 'Negotiations' ? V.ink : V.ink3, borderBottom: item.label === 'Negotiations' ? `2px solid ${V.gold}` : '2px solid transparent' }}
            onClick={() => item.path && navigate(item.path)}
          >{item.label}</div>
        ))}
      </div>
      <div style={s.navRight}>
        <span style={s.navUser}>{user?.email}</span>
        <button style={s.navSignOut} onClick={logout}>Sign out</button>
        <button style={s.navNewBtn} onClick={onNewProject}>+ New Negotiation</button>
      </div>
    </div>
  );
}

// ─── PROJECT MODAL (New + Edit) ───────────────────────────────────────────────

function ProjectModal({ project, onClose, onSuccess }) {
  const isEdit = !!project;
  const [form, setForm] = useState({
    name:         project?.name         || '',
    industry:     project?.industry     || 'maritime',
    status:       project?.status       || 'active',
    notes:        project?.notes        || '',
    project_code: project?.project_code || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError(null);
    try {
      if (isEdit) { await api.put(`/api/projects/${project.id}`, form); }
      else        { await api.post('/api/projects', form); }
      onSuccess();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const industries = [
    { val: 'maritime',     label: '⚓  Maritime',     ac: '#1a4d8f', ab: '#e8f0f8' },
    { val: 'construction', label: '🏗  Construction', ac: '#7a4a00', ab: '#fdf4e7' },
    { val: 'generic',      label: '◈  General',       ac: V.ink2,   ab: V.paper2  },
  ];

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        <div style={{ ...s.modalHeader, borderTop: `3px solid ${V.gold}` }}>
          <div>
            <div style={s.modalTitle}>{isEdit ? 'Edit negotiation' : 'New negotiation'}</div>
            <div style={s.modalSub}>{isEdit ? 'Update project details' : 'Create a new document workspace'}</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          <div style={s.field}>
            <label style={s.fieldLabel}>Name <span style={{ color: V.gold }}>*</span></label>
            <input style={s.input} placeholder="e.g. MV Poseidon — Dry Dock 2026" value={form.name}
              onChange={e => setF('name', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} autoFocus />
          </div>
          <div style={s.field}>
            <label style={s.fieldLabel}>Sector</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {industries.map(opt => (
                <div key={opt.val}
                  style={{ flex:1, padding:'10px 8px', borderRadius:4, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:500, fontFamily:V.sans, border:`1.5px solid ${form.industry === opt.val ? opt.ac : V.line}`, background: form.industry === opt.val ? opt.ab : V.paper, color: form.industry === opt.val ? opt.ac : V.ink3 }}
                  onClick={() => setF('industry', opt.val)}
                >{opt.label}</div>
              ))}
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div style={s.field}>
              <label style={s.fieldLabel}>Status</label>
              <select style={s.input} value={form.status} onChange={e => setF('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Project code <span style={{ color:V.ink4, fontWeight:400 }}>(optional)</span></label>
              <input style={s.input} placeholder="e.g. PRJ-2025-042" value={form.project_code} onChange={e => setF('project_code', e.target.value)} />
            </div>
          </div>
          <div style={s.field}>
            <label style={s.fieldLabel}>Notes <span style={{ color:V.ink4, fontWeight:400 }}>(optional)</span></label>
            <textarea style={{ ...s.input, resize:'vertical', minHeight:72 }} placeholder="Brief description…" value={form.notes} onChange={e => setF('notes', e.target.value)} />
          </div>
          {error && <div style={{ background:V.badSoft, border:`1px solid ${V.bad}`, borderRadius:4, padding:'10px 14px', fontSize:12, color:V.bad }}>{error}</div>}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...s.btnGold, opacity: saving ? 0.6 : 1 }} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Open negotiation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DELETE MODAL ─────────────────────────────────────────────────────────────

function DeleteModal({ project, onCancel, onConfirm, deleting }) {
  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={s.modal}>
        <div style={{ ...s.modalHeader, borderTop:`3px solid ${V.bad}` }}>
          <div>
            <div style={s.modalTitle}>Delete negotiation</div>
            <div style={s.modalSub}>Moved to Recently Deleted — recoverable for 30 days</div>
          </div>
          <button style={s.closeBtn} onClick={onCancel}>✕</button>
        </div>
        <div style={s.modalBody}>
          <div style={{ background:V.paper2, border:`1px solid ${V.line}`, borderRadius:4, padding:'12px 16px' }}>
            <div style={{ fontFamily:V.serif, fontSize:15, fontWeight:600, color:V.ink }}>{project.name}</div>
            <div style={{ fontSize:12, color:V.ink4, marginTop:4 }}>{project.document_count || 0} documents — preserved during recovery period</div>
          </div>
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnGhost} onClick={onCancel} disabled={deleting}>Cancel</button>
          <button style={{ ...s.btnDanger, opacity: deleting ? 0.6 : 1 }} onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Move to deleted'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CARD MENU ────────────────────────────────────────────────────────────────

function CardMenu({ project, onOpen, onEdit, onDelete, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const item = (label, action, danger = false) => (
    <div
      style={{ ...s.menuItem, color: danger ? V.bad : V.ink2 }}
      onMouseEnter={e => e.currentTarget.style.background = danger ? V.badSoft : V.paper2}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      onClick={e => { e.stopPropagation(); action(); onClose(); }}
    >{label}</div>
  );

  return (
    <div ref={ref} style={s.menu}>
      {item('Open project', onOpen)}
      <div style={s.menuDivider}></div>
      {item('Rename / Edit', onEdit)}
      <div style={s.menuDivider}></div>
      {item('Delete', onDelete, true)}
    </div>
  );
}

// ─── PROJECT CARD ─────────────────────────────────────────────────────────────

function ProjectCard({ project, onClick, onEdit, onDelete }) {
  const [hovered, setHovered]   = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const indCfg = {
    maritime:     { label:'Maritime',     icon:'⚓', color:'#1a4d8f', bg:'#dbeafe' },
    construction: { label:'Construction', icon:'🏗', color:'#7a4a00', bg:'#fef3c7' },
    generic:      { label:'General',      icon:'◈', color:V.ink2,   bg:V.paper2  },
  }[project.industry] || { label:'General', icon:'◈', color:V.ink2, bg:V.paper2 };

  const sc          = getStatusCfg(project.status);
  const isEmpty     = project.document_count === 0;
  const hasInsight  = project.ai_insight && !isEmpty;
  const hasAlert    = project.has_expiring;
  const contractVal = project.contract_value ? `€${Number(project.contract_value).toLocaleString('en-GB')}` : null;

  return (
    <div
      style={{ ...s.card, boxShadow: hovered ? '0 4px 20px rgba(12,24,48,0.08)' : 'none', transform: hovered ? 'translateY(-1px)' : 'none' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={s.cardHead}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ ...s.industryBadge, color:indCfg.color, background:indCfg.bg }}>
            {indCfg.icon} {indCfg.label.toUpperCase()}
          </span>
          <span style={{ fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:3, color:sc.color, background:sc.bg, fontFamily:V.mono, letterSpacing:0.5 }}>
            {sc.label.toUpperCase()}
          </span>
        </div>
        <div style={{ position:'relative' }}>
          <button
            style={{ ...s.menuTrigger, opacity: hovered || menuOpen ? 1 : 0, background: menuOpen ? V.paper2 : 'transparent' }}
            onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
          >···</button>
          {menuOpen && (
            <CardMenu
              project={project}
              onOpen={onClick}
              onEdit={() => onEdit(project)}
              onDelete={() => onDelete(project)}
              onClose={() => setMenuOpen(false)}
            />
          )}
        </div>
      </div>

      <div style={s.cardName} onClick={onClick}>{project.name}</div>

      {isEmpty ? (
        <div style={s.insightMuted} onClick={onClick}>No documents yet — upload to activate AI analysis.</div>
      ) : hasAlert ? (
        <div style={{ ...s.insight, borderLeftColor:V.warn, background:'#f5ecd9' }} onClick={onClick}>
          <span style={{ color:V.warn }} dangerouslySetInnerHTML={{ __html: project.alert_text || 'Quotation expiring soon.' }} />
        </div>
      ) : hasInsight ? (
        <div style={s.insight} onClick={onClick}>
          <span style={{ color:V.ink2 }} dangerouslySetInnerHTML={{ __html: project.ai_insight }} />
        </div>
      ) : (
        <div style={s.insightMuted} onClick={onClick}>
          {project.document_count} document{project.document_count !== 1 ? 's' : ''} uploaded — add more to unlock AI insights.
        </div>
      )}

      <div style={s.cardDivider}></div>

      <div style={s.cardStats} onClick={onClick}>
        <div style={s.stat}>
          <div style={s.statVal}>{project.document_count || 0}</div>
          <div style={s.statLabel}>docs</div>
        </div>
        {project.supplier_count > 0 && (<>
          <div style={s.statDivider}></div>
          <div style={s.stat}>
            <div style={s.statVal}>{project.supplier_count}</div>
            <div style={s.statLabel}>suppliers</div>
          </div>
        </>)}
        {contractVal && (<>
          <div style={s.statDivider}></div>
          <div style={s.stat}>
            <div style={{ ...s.statVal, color:V.gold }}>{contractVal}</div>
            <div style={s.statLabel}>contract value</div>
          </div>
        </>)}
        <div style={{ marginLeft:'auto', fontSize:12, color: hovered ? V.ink2 : V.ink4, transition:'color 0.15s', fontFamily:V.sans }}>
          Enter the room →
        </div>
      </div>
    </div>
  );
}

// ─── RECENTLY DELETED ─────────────────────────────────────────────────────────

function RecentlyDeleted({ onRestored }) {
  const [deleted, setDeleted]     = useState([]);
  const [open, setOpen]           = useState(false);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    api.get('/api/projects/deleted').then(r => setDeleted(r.data.projects || [])).catch(() => {});
  }, []);

  if (deleted.length === 0) return null;

  const handleRestore = async (id) => {
    setRestoring(id);
    try {
      await api.post(`/api/projects/${id}/restore`);
      setDeleted(d => d.filter(p => p.id !== id));
      onRestored();
    } catch { alert('Restore failed.'); }
    finally { setRestoring(null); }
  };

  return (
    <div style={{ marginTop:8 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'12px 0', borderTop:`1px solid ${V.line}` }} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize:9, fontWeight:700, letterSpacing:2, color:V.ink4, textTransform:'uppercase', fontFamily:V.sans }}>Recently Deleted</span>
        <span style={{ fontSize:11, color:V.ink4, background:V.paper3, padding:'1px 7px', borderRadius:10, fontFamily:V.mono }}>{deleted.length}</span>
        <span style={{ marginLeft:'auto', fontSize:11, color:V.ink4 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ display:'flex', flexDirection:'column', gap:8, paddingBottom:24 }}>
          {deleted.map(p => (
            <div key={p.id} style={{ background:V.paper, border:`0.5px solid ${V.line}`, borderRadius:4, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:13, fontWeight:500, color:V.ink3, fontFamily:V.sans, textDecoration:'line-through' }}>{p.name}</div>
                <div style={{ fontSize:11, color:V.ink4, marginTop:2, fontFamily:V.sans }}>{p.document_count || 0} docs · {p.days_remaining ?? 30} day{p.days_remaining !== 1 ? 's' : ''} left to recover</div>
              </div>
              <button style={{ ...s.btnGhost, padding:'6px 14px', fontSize:12, opacity: restoring === p.id ? 0.6 : 1 }} onClick={() => handleRestore(p.id)} disabled={restoring === p.id}>
                {restoring === p.id ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function Projects() {
  const [enriched, setEnriched]           = useState([]);
  const [filter, setFilter]               = useState('all');
  const [loading, setLoading]             = useState(true);
  const [modalProject, setModalProject]   = useState(null);
  const [deleteProject, setDeleteProject] = useState(null);
  const [deleting, setDeleting]           = useState(false);
  const [restoreKey, setRestoreKey]       = useState(0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const loadProjects = () => {
    setLoading(true);
    api.get('/api/projects')
      .then(res => enrichProjects(res.data.projects || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const enrichProjects = async (raw) => {
    const result = await Promise.all(raw.map(async (p) => {
      try {
        const docsRes   = await api.get(`/api/documents?project_id=${p.id}`);
        const docs      = docsRes.data.documents || [];
        const awarded   = docs.filter(d => d.awarded);
        const contractValue = awarded.reduce((sum, d) => sum + (d.total_value || 0), 0);
        const suppliers = [...new Set(docs.map(d => d.supplier).filter(Boolean))];
        const hasExpiring = docs.some(d => d.validity_date && new Date(d.validity_date) < new Date(Date.now() + 14 * 24 * 60 * 60 * 1000) && new Date(d.validity_date) > new Date());

        let ai_insight = null, alert_text = null;
        if (docs.length >= 2) {
          const amendments = docs.filter(d => d.document_type === 'Amendment');
          const contracts  = docs.filter(d => d.document_type === 'Contract');
          const quotations = docs.filter(d => d.document_type === 'Quotation' || d.document_type === 'Proposal');
          if (amendments.length > 0 && contracts.length > 0 && contracts[contracts.length-1]?.total_value && amendments[0]?.total_value) {
            const diff = amendments[0].total_value - contracts[contracts.length-1].total_value;
            const pct  = ((diff / contracts[contracts.length-1].total_value) * 100).toFixed(1);
            const sign = diff >= 0 ? '+' : '';
            ai_insight = `Contract awarded to <strong>${awarded[0]?.supplier || contracts[0]?.supplier || 'supplier'}</strong>. ${amendments.length} amendment${amendments.length > 1 ? 's' : ''} raised value by <strong>${sign}€${Math.abs(diff).toLocaleString('en-GB')}</strong> (${sign}${pct}%).`;
          } else if (quotations.length >= 2) {
            const sorted = [...quotations].filter(q => q.total_value).sort((a, b) => a.total_value - b.total_value);
            if (sorted.length >= 2) {
              const diff = sorted[sorted.length-1].total_value - sorted[0].total_value;
              const pct  = ((diff / sorted[sorted.length-1].total_value) * 100).toFixed(1);
              ai_insight = `<strong>${sorted[0].supplier || 'Best offer'}</strong> is €${diff.toLocaleString('en-GB')} (${pct}%) lower than highest quote.`;
            }
          } else if (awarded.length > 0) {
            ai_insight = `Contract awarded to <strong>${awarded[0].supplier}</strong> at €${Number(awarded[0].total_value).toLocaleString('en-GB')}.`;
          }
        }
        if (hasExpiring) {
          const exp  = docs.find(d => d.validity_date && new Date(d.validity_date) < new Date(Date.now() + 14*24*60*60*1000) && new Date(d.validity_date) > new Date());
          const days = exp ? Math.ceil((new Date(exp.validity_date) - new Date()) / (1000*60*60*24)) : 0;
          alert_text = `<strong>Quotation expiring in ${days} day${days !== 1 ? 's' : ''}.</strong> Review ${exp?.supplier || ''} offer before it lapses.`;
        }
        return { ...p, document_count: docs.length, contract_value: contractValue || null, supplier_count: suppliers.length, awarded_count: awarded.length, has_expiring: hasExpiring, ai_insight, alert_text };
      } catch { return { ...p, document_count: p.document_count || 0, supplier_count: 0, awarded_count: 0 }; }
    }));
    setEnriched(result);
  };

  useEffect(() => { loadProjects(); }, []);

  const handleDelete = async () => {
    if (!deleteProject) return;
    setDeleting(true);
    try {
      await api.delete(`/api/projects/${deleteProject.id}`);
      setDeleteProject(null);
      setRestoreKey(k => k + 1);
      loadProjects();
    } catch { alert('Delete failed.'); }
    finally { setDeleting(false); }
  };

  const allCount      = enriched.length;
  const maritimeCount = enriched.filter(p => p.industry === 'maritime').length;
  const constrCount   = enriched.filter(p => p.industry === 'construction').length;
  const activeCount   = enriched.filter(p => p.status === 'active').length;
  const totalDocs     = enriched.reduce((a, p) => a + (p.document_count || 0), 0);
  const totalValue    = enriched.reduce((a, p) => a + (p.contract_value || 0), 0);
  const pendingCount  = enriched.filter(p => p.document_count >= 2 && !p.awarded_count).length;

  const filterTabs = [
    { key:'all',          label:'All',         count: allCount },
    { key:'maritime',     label:'Maritime',     count: maritimeCount },
    { key:'construction', label:'Construction', count: constrCount },
    { key:'active',       label:'Active',       count: activeCount },
  ];

  const visible = enriched.filter(p => {
    if (filter === 'maritime')     return p.industry === 'maritime';
    if (filter === 'construction') return p.industry === 'construction';
    if (filter === 'active')       return p.status === 'active';
    return true;
  });

  return (
    <div style={s.wrap}>
      <FontLoader />
      <LiveTicker projects={enriched} />
      <TopNav user={user} logout={logout} onNewProject={() => setModalProject(false)} />

      <div style={s.body}>
        <div style={s.pageHead}>
          <div>
            <div style={s.pageEyebrow}>NEGOTIATION DESK · Q2 2026</div>
            <div style={s.pageTitle}>{loading ? 'Loading…' : `${allCount} open negotiation${allCount !== 1 ? 's' : ''}.`}</div>
            <div style={s.pageSub}>A summary of every supplier proposal on the table — with AI-prepared verdicts, flags, and recommended next moves.</div>
          </div>
          <div style={{ display:'flex', gap:10, alignSelf:'flex-end' }}>
            <button style={s.btnGhost}>Export brief</button>
            <button style={s.btnGold} onClick={() => setModalProject(false)}>+ New Negotiation</button>
          </div>
        </div>

        <div style={s.kpiStrip}>
          {[
            { label:'ACTIVE',             value: activeCount,  sub:`${allCount} total` },
            { label:'DOCUMENTS ANALYSED', value: totalDocs,    sub:`across all projects` },
            { label:'AWARDED YTD',        value: totalValue > 0 ? `€${(totalValue/1000000).toFixed(2)}M` : '—', sub:'contract value', accent: totalValue > 0 },
            { label:'PENDING VERDICT',    value: pendingCount, sub:`${pendingCount} closeable on current evidence` },
          ].map((k, i) => (
            <div key={i} style={{ ...s.kpi, borderTop: k.accent ? `2px solid ${V.gold}` : `2px solid ${V.line}` }}>
              <div style={s.kpiLabel}>{k.label}</div>
              <div style={{ ...s.kpiVal, color: k.accent ? V.gold : V.ink }}>{k.value}</div>
              <div style={s.kpiSub}>{k.sub}</div>
            </div>
          ))}
        </div>

        <div style={s.filterBar}>
          <div style={s.filterTabs}>
            {filterTabs.map(tab => (
              <button key={tab.key}
                style={{ ...s.filterTab, color: filter === tab.key ? V.ink : V.ink4, borderBottom: filter === tab.key ? `2px solid ${V.ink}` : '2px solid transparent', fontWeight: filter === tab.key ? 600 : 400 }}
                onClick={() => setFilter(tab.key)}
              >
                {tab.label}
                {tab.count > 0 && <span style={{ marginLeft:6, fontSize:10, color: filter === tab.key ? V.ink3 : V.ink4 }}>({tab.count})</span>}
              </button>
            ))}
          </div>
          <div style={s.filterCount}>SORTED BY URGENCY · {visible.length} OF {allCount}</div>
        </div>

        {loading ? (
          <div style={s.loading}>Loading negotiations…</div>
        ) : (
          <div style={s.grid}>
            {visible.map(project => (
              <ProjectCard key={project.id} project={project}
                onClick={() => navigate(`/projects/${project.id}`)}
                onEdit={p => setModalProject(p)}
                onDelete={p => setDeleteProject(p)}
              />
            ))}
            <div style={s.newCard}
              onClick={() => setModalProject(false)}
              onMouseEnter={e => { e.currentTarget.style.borderColor = V.ink3; e.currentTarget.style.background = V.paper; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = V.line; e.currentTarget.style.background = V.paper2; }}
            >
              <div style={s.newCardPlus}>+</div>
              <div style={s.newCardLabel}>New Negotiation</div>
            </div>
          </div>
        )}

        <RecentlyDeleted key={restoreKey} onRestored={loadProjects} />
      </div>

      {modalProject !== null && (
        <ProjectModal project={modalProject || null} onClose={() => setModalProject(null)} onSuccess={() => { setModalProject(null); loadProjects(); }} />
      )}
      {deleteProject && (
        <DeleteModal project={deleteProject} onCancel={() => setDeleteProject(null)} onConfirm={handleDelete} deleting={deleting} />
      )}
    </div>
  );
}

const s = {
  wrap:         { minHeight:'100vh', background:V.paper2, fontFamily:V.sans, color:V.ink },
  utilBar:      { background:V.ink, height:32, display:'flex', alignItems:'center', padding:'0 24px', gap:0, overflow:'hidden' },
  utilLeft:     { display:'flex', alignItems:'center', gap:8, paddingRight:16, borderRight:'1px solid rgba(255,255,255,0.1)', marginRight:12, flexShrink:0 },
  utilDot:      { width:6, height:6, borderRadius:'50%', background:V.gold },
  tickerLabel:  { fontSize:9, fontWeight:700, letterSpacing:2, color:V.gold, marginRight:12, flexShrink:0, textTransform:'uppercase' },
  tickerWrap:   { flex:1, overflow:'hidden', position:'relative' },
  tickerInner:  { display:'flex', whiteSpace:'nowrap', willChange:'transform' },
  tickerItem:   { display:'inline-flex', alignItems:'center' },
  tickerText:   { fontSize:11, fontFamily:V.sans },
  tickerSep:    { fontSize:11, color:'rgba(255,255,255,0.2)', margin:'0 20px' },
  nav:          { background:V.paper, borderBottom:`1px solid ${V.line}`, height:60, display:'flex', alignItems:'center', padding:'0 24px', gap:0 },
  wordmark:     { display:'flex', alignItems:'baseline', fontFamily:V.serif, fontSize:26, fontWeight:600, letterSpacing:-0.5, lineHeight:1 },
  wordmarkV:    { color:V.gold },
  wordmarkRest: { color:V.ink },
  wordmarkDivider:{ width:1, height:18, background:V.line, margin:'0 12px' },
  wordmarkTag:  { fontSize:10, fontWeight:500, letterSpacing:2.5, textTransform:'uppercase', color:V.ink3, marginRight:32, whiteSpace:'nowrap' },
  navItems:     { display:'flex', gap:0, height:60, alignItems:'center' },
  navItem:      { padding:'0 16px', fontSize:13, fontWeight:500, cursor:'pointer', height:60, display:'flex', alignItems:'center', transition:'color 0.15s', letterSpacing:0.1 },
  navRight:     { marginLeft:'auto', display:'flex', alignItems:'center', gap:14 },
  navUser:      { fontSize:12, color:V.ink4 },
  navSignOut:   { background:'none', border:'none', fontSize:12, color:V.ink3, cursor:'pointer', padding:0 },
  navNewBtn:    { background:V.ink, color:V.paper, border:'none', borderRadius:4, padding:'8px 16px', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:V.sans, letterSpacing:0.1 },
  body:         { padding:'36px 48px 80px', maxWidth:1440, margin:'0 auto', width:'100%', display:'flex', flexDirection:'column', gap:28 },
  pageHead:     { display:'flex', justifyContent:'space-between', alignItems:'flex-end', paddingBottom:24, borderBottom:`1px solid ${V.line}` },
  pageEyebrow:  { fontSize:10, fontWeight:500, letterSpacing:2.5, textTransform:'uppercase', color:V.ink3, marginBottom:8, fontFamily:V.sans },
  pageTitle:    { fontFamily:V.serif, fontSize:42, fontWeight:400, color:V.ink, lineHeight:1.12, letterSpacing:-0.5, marginBottom:10 },
  pageSub:      { fontSize:13, color:V.ink3, lineHeight:1.5, maxWidth:480 },
  kpiStrip:     { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:1, background:V.line, border:`1px solid ${V.line}`, borderRadius:4, overflow:'hidden' },
  kpi:          { background:V.paper, padding:'20px 24px' },
  kpiLabel:     { fontSize:9, fontWeight:600, letterSpacing:2, color:V.ink4, marginBottom:10, textTransform:'uppercase', fontFamily:V.sans },
  kpiVal:       { fontFamily:V.mono, fontSize:34, fontWeight:500, lineHeight:1, letterSpacing:-1 },
  kpiSub:       { fontSize:11, marginTop:6, fontFamily:V.sans, color:V.ink4 },
  filterBar:    { display:'flex', alignItems:'center', borderBottom:`1px solid ${V.line}` },
  filterTabs:   { display:'flex', gap:0 },
  filterTab:    { background:'none', border:'none', borderBottom:'2px solid transparent', padding:'10px 18px', fontSize:13, cursor:'pointer', fontFamily:V.sans, transition:'all 0.15s', marginBottom:-1 },
  filterCount:  { marginLeft:'auto', fontSize:10, fontWeight:600, letterSpacing:1.5, color:V.ink4, textTransform:'uppercase' },
  grid:         { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 },
  loading:      { color:V.ink4, fontSize:14, padding:'60px 0', textAlign:'center', fontFamily:V.sans },
  card:         { background:V.paper, border:`0.5px solid ${V.line}`, borderRadius:6, padding:'18px 20px', cursor:'pointer', transition:'all 0.18s', display:'flex', flexDirection:'column', gap:12, position:'relative' },
  cardHead:     { display:'flex', alignItems:'center', justifyContent:'space-between' },
  industryBadge:{ fontFamily:V.mono, fontSize:9, fontWeight:500, letterSpacing:1, padding:'3px 8px', borderRadius:3 },
  menuTrigger:  { background:'transparent', border:`0.5px solid ${V.line}`, borderRadius:4, padding:'2px 8px', fontSize:14, cursor:'pointer', color:V.ink3, letterSpacing:2, lineHeight:1, transition:'opacity 0.15s', fontFamily:V.mono },
  cardName:     { fontFamily:V.serif, fontSize:16, fontWeight:500, color:V.ink, lineHeight:1.35 },
  insight:      { borderLeft:`2px solid ${V.ink2}`, background:'#f0ede4', padding:'8px 12px', borderRadius:'0 4px 4px 0', fontSize:12, lineHeight:1.5, color:V.ink2 },
  insightMuted: { borderLeft:`2px solid ${V.line}`, background:V.paper2, padding:'8px 12px', borderRadius:'0 4px 4px 0', fontSize:12, color:V.ink4, lineHeight:1.5 },
  cardDivider:  { height:1, background:V.line },
  cardStats:    { display:'flex', alignItems:'center', gap:12 },
  stat:         { display:'flex', flexDirection:'column', gap:1 },
  statVal:      { fontFamily:V.mono, fontSize:14, fontWeight:500, color:V.ink },
  statLabel:    { fontSize:9, color:V.ink4, textTransform:'uppercase', letterSpacing:1, fontFamily:V.sans },
  statDivider:  { width:1, height:24, background:V.line },
  newCard:      { background:V.paper2, border:`0.5px dashed ${V.line}`, borderRadius:6, padding:'18px 20px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:200, gap:6, transition:'all 0.18s' },
  newCardPlus:  { width:32, height:32, borderRadius:'50%', background:V.paper3, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, color:V.ink4, marginBottom:4 },
  newCardLabel: { fontSize:13, fontWeight:500, color:V.ink3, fontFamily:V.sans },
  menu:         { position:'absolute', top:32, right:0, background:V.paper, border:`0.5px solid ${V.line}`, borderRadius:6, padding:'4px 0', zIndex:20, minWidth:160, boxShadow:'0 8px 24px rgba(12,24,48,0.12)' },
  menuItem:     { padding:'9px 16px', fontSize:13, cursor:'pointer', fontFamily:V.sans, transition:'background 0.1s' },
  menuDivider:  { height:1, background:V.line, margin:'3px 0' },
  overlay:      { position:'fixed', inset:0, background:'rgba(12,24,48,0.55)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
  modal:        { background:V.paper, borderRadius:6, width:'100%', maxWidth:480, display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 32px 64px rgba(12,24,48,0.22)' },
  modalHeader:  { padding:'22px 26px 18px', borderBottom:`1px solid ${V.line}`, display:'flex', alignItems:'flex-start', justifyContent:'space-between' },
  modalTitle:   { fontFamily:V.serif, fontSize:20, fontWeight:500, color:V.ink },
  modalSub:     { fontSize:12, color:V.ink4, marginTop:3, fontFamily:V.sans },
  closeBtn:     { background:'none', border:'none', color:V.ink4, fontSize:16, cursor:'pointer', padding:4, fontFamily:V.sans },
  modalBody:    { padding:'22px 26px', display:'flex', flexDirection:'column', gap:18, background:V.paper2 },
  modalFooter:  { padding:'16px 26px', borderTop:`1px solid ${V.line}`, display:'flex', justifyContent:'flex-end', gap:10, background:V.paper },
  field:        { display:'flex', flexDirection:'column', gap:6 },
  fieldLabel:   { fontSize:10, fontWeight:600, letterSpacing:1.5, textTransform:'uppercase', color:V.ink3, fontFamily:V.sans },
  input:        { background:V.paper, border:`1px solid ${V.line}`, borderRadius:4, padding:'9px 12px', fontSize:13, color:V.ink, fontFamily:V.sans, outline:'none' },
  btnGold:      { background:V.gold, color:V.ink, border:'none', borderRadius:4, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:V.sans, letterSpacing:0.1 },
  btnGhost:     { background:'transparent', color:V.ink, border:`1px solid ${V.line}`, borderRadius:4, padding:'9px 20px', fontSize:13, fontWeight:400, cursor:'pointer', fontFamily:V.sans },
  btnDanger:    { background:V.bad, color:V.paper, border:'none', borderRadius:4, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:V.sans },
};
